/**
 * AmbassadorsPage — the public Brand Ambassador recruitment landing page.
 *
 * Lives at /ambassadors. A persuasive, editorial-luxe pitch ("sell what you
 * love, earn doubled referral cash") with an interactive earnings calculator
 * and an inline application form that enrols the visitor as a Brand Ambassador
 * (role='ambassador', 12-month 2× window) via POST /api/ambassadors/join.
 *
 * Admins mint QR codes that deep-link here with `?invite=<code>` — when present
 * and valid, the hero shows a personalised "you've been invited" badge and the
 * code is attributed on join (so the recruiter is credited).
 *
 * Numbers shown here mirror the real engine: L1 3% → doubled to 6% for the
 * ambassador's first year (then base 3/2/1% across three levels), paid as
 * referral cash redeemable at checkout.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';
import { useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { toast } from 'react-toastify';
import { Icon } from '../components/ui/Icon';

// ─── Tiny scroll-reveal hook (CSS does the animating via .cash-reveal) ─────────
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = root.querySelectorAll('.cash-reveal');
    if (typeof IntersectionObserver === 'undefined') {
      els.forEach(el => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

const money = (n) => '$' + Math.round(n).toLocaleString();

// ─── Hero ──────────────────────────────────────────────────────────────────────
function Hero({ invite, onApply }) {
  return (
    <section className="relative overflow-hidden bg-tea-50 polka-pink">
      {/* soft radial glow + gradient wash */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full bg-tea-200/50 blur-3xl" />
        <div className="absolute -bottom-40 -left-24 w-[30rem] h-[30rem] rounded-full bg-tea-100/70 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="cash-reveal">
          {invite?.valid ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-tea-600 text-white text-[11px] font-semibold uppercase tracking-[0.18em] px-4 py-1.5 shadow-sm">
              <Icon name="sparkles" className="w-3.5 h-3.5" /> {invite.referrerName ? `${invite.referrerName} invited you` : 'You’ve been personally invited'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur text-tea-700 text-[11px] font-semibold uppercase tracking-[0.18em] px-4 py-1.5 ring-1 ring-tea-200">
              The AB8 Beauty Ambassador Program
            </span>
          )}
        </div>

        <h1 className="cash-reveal font-display text-[2.9rem] leading-[1.04] sm:text-6xl md:text-7xl font-semibold text-gray-900 mt-6 max-w-4xl tracking-tight">
          Get paid to share the glow you already
          <span className="italic text-tea-600"> can&rsquo;t stop talking about.</span>
        </h1>

        <p className="cash-reveal text-lg md:text-xl text-gray-600 mt-6 max-w-2xl leading-relaxed" data-delay="1">
          Become an AB8 Beauty Brand Ambassador. Share your link, earn
          <strong className="text-gray-900"> 3% cash on every referral</strong> across three
          levels — and for your <strong className="text-tea-700">entire first year we double it to 6%.</strong>
          Plus ambassador-only pricing on everything you love.
        </p>

        <div className="cash-reveal flex flex-col sm:flex-row gap-3 mt-9" data-delay="2">
          <button onClick={onApply} className="btn-primary text-base px-8 py-4 shadow-lg shadow-tea-600/20">
            Become an Ambassador — free
          </button>
          <a href="#earnings" className="btn-secondary text-base px-8 py-4 bg-white/70 backdrop-blur">
            Show me the numbers
          </a>
        </div>

        {/* floating proof chips */}
        <div className="cash-reveal grid grid-cols-2 sm:grid-cols-4 gap-3 mt-14 max-w-3xl" data-delay="3">
          {[
            { k: '6%', v: 'First-year referral cash', s: 'doubled from 3%' },
            { k: '3 levels', v: 'Deep network rewards', s: '6 / 4 / 2% year one' },
            { k: '$0', v: 'To join, ever', s: 'no fees, no inventory' },
            { k: '12 mo', v: 'Doubled rewards window', s: 'starts the day you join' },
          ].map((c) => (
            <div key={c.v} className="rounded-2xl bg-white/80 backdrop-blur ring-1 ring-tea-100 px-4 py-4 shadow-sm">
              <div className="font-display text-3xl font-semibold text-tea-700">{c.k}</div>
              <div className="text-[13px] font-semibold text-gray-800 mt-1 leading-snug">{c.v}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{c.s}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Value props ────────────────────────────────────────────────────────────────
const PROPS = [
  { icon: 'commissions', title: 'Doubled the entire first year', body: 'Your first 12 months pay 6% on direct referrals — twice the standard rate — automatically, with our special ambassador link.' },
  { icon: 'leaf', title: 'Three levels of passive cash', body: 'Earn on the people you bring in, the people they bring in, and one level deeper. Build a network that pays you while you sleep.' },
  { icon: 'pricing', title: 'Ambassador-only pricing', body: 'Every commission lands as referral cash redeemable at checkout — stack it for huge discounts (up to 100% off) on the products you already buy.' },
  { icon: 'mobile', title: 'Share in one tap', body: 'Get a personal link and a scannable QR code the moment you join. Drop it on a story, a flyer, or your link-in-bio and start earning.' },
];

function ValueProps() {
  const ref = useReveal();
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
      <p className="cash-reveal text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-tea-600">Why ambassadors love it</p>
      <h2 className="cash-reveal font-display text-4xl md:text-5xl font-semibold text-center text-gray-900 mt-3 max-w-3xl mx-auto">
        A side income that feels like sharing a secret
      </h2>
      <div className="grid sm:grid-cols-2 gap-5 mt-14">
        {PROPS.map((p, i) => (
          <div key={p.title} className="cash-reveal group relative rounded-3xl bg-white border border-gray-100 p-8 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all" data-delay={String((i % 4) + 1)}>
            <div className="grid place-items-center w-14 h-14 rounded-2xl bg-tea-50 text-tea-600 ring-1 ring-tea-100"><Icon name={p.icon} className="w-6 h-6" /></div>
            <h3 className="font-display text-2xl font-semibold text-gray-900 mt-5">{p.title}</h3>
            <p className="text-gray-600 mt-2 leading-relaxed">{p.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Earnings calculator ─────────────────────────────────────────────────────────
function EarningsCalculator({ onApply }) {
  const ref = useReveal();
  const [customers, setCustomers] = useState(12);
  const [avgOrder, setAvgOrder]   = useState(60);
  const [perMonth, setPerMonth]   = useState(1);

  // First-year direct (L1) rate is doubled: 6%.
  const monthlySpend = customers * avgOrder * perMonth;
  const firstYearCash = monthlySpend * 12 * 0.06;     // 6% doubled, year one
  const afterCash     = monthlySpend * 12 * 0.03;     // 3% base, after year one
  const networkBonus  = firstYearCash * 0.5;          // illustrative L2+L3 upside

  const Slider = ({ label, value, set, min, max, step, fmt }) => (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <span className="font-display text-2xl font-semibold text-tea-700">{fmt(value)}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => set(Number(e.target.value))}
        className="w-full mt-2 accent-tea-600"
      />
    </div>
  );

  return (
    <section id="earnings" ref={ref} className="bg-gradient-to-b from-white to-tea-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="text-center">
          <p className="cash-reveal text-[11px] font-semibold uppercase tracking-[0.22em] text-tea-600">Run your numbers</p>
          <h2 className="cash-reveal font-display text-4xl md:text-5xl font-semibold text-gray-900 mt-3">
            What could your first year look like?
          </h2>
        </div>

        <div className="cash-reveal grid lg:grid-cols-2 gap-6 mt-14 items-stretch" data-delay="1">
          {/* Controls */}
          <div className="rounded-3xl bg-white border border-gray-100 p-8 shadow-sm space-y-7">
            <Slider label="People you refer" value={customers} set={setCustomers} min={1} max={100} step={1} fmt={(v) => `${v}`} />
            <Slider label="Average order" value={avgOrder} set={setAvgOrder} min={20} max={250} step={5} fmt={money} />
            <Slider label="Orders each / month" value={perMonth} set={setPerMonth} min={1} max={4} step={1} fmt={(v) => `${v}×`} />
            <p className="text-xs text-gray-400 leading-relaxed">
              Estimates only, for illustration. Referral cash is earned when orders ship and is
              redeemable at checkout. First-year direct rate is 6% (2× the standard 3%).
            </p>
          </div>

          {/* Result */}
          <div className="relative rounded-3xl bg-tea-700 text-white p-8 shadow-xl overflow-hidden">
            <div className="absolute inset-0 polka-white opacity-10" />
            <div className="relative">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-tea-100">Estimated year-one referral cash</div>
              <div className="font-display text-6xl md:text-7xl font-semibold mt-2 leading-none cash-pulse">{money(firstYearCash)}</div>
              <div className="text-tea-100 mt-2 text-sm">on {money(monthlySpend)} of monthly orders from your circle</div>

              <div className="grid grid-cols-2 gap-3 mt-8">
                <div className="rounded-2xl bg-white/10 px-4 py-4 ring-1 ring-white/15">
                  <div className="text-[11px] uppercase tracking-wide text-tea-100">+ Network upside</div>
                  <div className="font-display text-2xl font-semibold mt-1">{money(networkBonus)}</div>
                  <div className="text-[11px] text-tea-100/80">levels 2 & 3, growing</div>
                </div>
                <div className="rounded-2xl bg-white/10 px-4 py-4 ring-1 ring-white/15">
                  <div className="text-[11px] uppercase tracking-wide text-tea-100">After year one</div>
                  <div className="font-display text-2xl font-semibold mt-1">{money(afterCash)}</div>
                  <div className="text-[11px] text-tea-100/80">still 3% direct, forever</div>
                </div>
              </div>

              <button onClick={onApply} className="mt-8 w-full inline-flex items-center justify-center rounded-lg bg-white text-tea-700 font-bold px-6 py-3.5 hover:bg-tea-50 transition-colors">
                Lock in my doubled year →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── How it works ────────────────────────────────────────────────────────────────
const STEPS = [
  { n: '01', t: 'Apply in 60 seconds', d: 'Fill in the short form below. No fees, no inventory to buy, no quotas. Approval is instant.' },
  { n: '02', t: 'Get your link & QR', d: 'We hand you a personal referral link and a scannable QR code the moment you join. Share them anywhere.' },
  { n: '03', t: 'Earn doubled cash', d: 'Every order through your link pays you referral cash — doubled all first year — redeemable for products or cashed out.' },
];

function HowItWorks() {
  const ref = useReveal();
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
      <h2 className="cash-reveal font-display text-4xl md:text-5xl font-semibold text-center text-gray-900">
        Three steps to your first payout
      </h2>
      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {STEPS.map((s, i) => (
          <div key={s.n} className="cash-reveal relative rounded-3xl bg-white border border-gray-100 p-8 shadow-sm" data-delay={String(i + 1)}>
            <div className="font-display text-6xl font-semibold text-tea-100">{s.n}</div>
            <h3 className="font-display text-2xl font-semibold text-gray-900 mt-2">{s.t}</h3>
            <p className="text-gray-600 mt-2 leading-relaxed">{s.d}</p>
            {i < STEPS.length - 1 && (
              <div className="hidden md:block absolute top-1/2 -right-3 text-tea-300 text-2xl">→</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Commission tiers ─────────────────────────────────────────────────────────────
function Tiers() {
  const ref = useReveal();
  const rows = [
    { level: 'Level 1', who: 'People you refer directly', base: '3%', boosted: '6%' },
    { level: 'Level 2', who: 'The people they refer', base: '2%', boosted: '4%' },
    { level: 'Level 3', who: 'One level deeper', base: '1%', boosted: '2%' },
  ];
  return (
    <section ref={ref} className="bg-tea-900 text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="text-center">
          <p className="cash-reveal text-[11px] font-semibold uppercase tracking-[0.22em] text-tea-300">Transparent, generous, stacked</p>
          <h2 className="cash-reveal font-display text-4xl md:text-5xl font-semibold mt-3">Your commission, three levels deep</h2>
          <p className="cash-reveal text-tea-100/80 mt-4 max-w-2xl mx-auto">
            Standard rates are already among the best in beauty. As a first-year ambassador, every
            number on the right is what you actually earn.
          </p>
        </div>

        <div className="cash-reveal mt-12 rounded-3xl bg-white/5 ring-1 ring-white/10 overflow-hidden" data-delay="1">
          <div className="grid grid-cols-12 px-6 py-4 text-[11px] uppercase tracking-wider text-tea-200 border-b border-white/10">
            <div className="col-span-5">Tier</div>
            <div className="col-span-3 text-center">Standard</div>
            <div className="col-span-4 text-center">Your first year</div>
          </div>
          {rows.map((r) => (
            <div key={r.level} className="grid grid-cols-12 items-center px-6 py-5 border-b border-white/5 last:border-0">
              <div className="col-span-5">
                <div className="font-semibold">{r.level}</div>
                <div className="text-sm text-tea-100/70">{r.who}</div>
              </div>
              <div className="col-span-3 text-center font-display text-2xl text-tea-200/70 line-through decoration-tea-400/50">{r.base}</div>
              <div className="col-span-4 text-center">
                <span className="font-display text-3xl md:text-4xl font-semibold text-white">{r.boosted}</span>
                <span className="ml-2 text-[10px] font-bold uppercase bg-tea-500 text-white rounded-full px-2 py-0.5 align-middle">2×</span>
              </div>
            </div>
          ))}
        </div>
        <p className="cash-reveal text-center text-tea-100/60 text-sm mt-6">
          After your first year you keep the full standard 3 / 2 / 1% — forever.
        </p>
      </div>
    </section>
  );
}

// ─── Social proof ────────────────────────────────────────────────────────────────
const QUOTES = [
  { q: 'I shared my link in one story and woke up to referral cash. It genuinely pays for my own skincare now.', n: 'Maya R.', r: 'Ambassador since launch' },
  { q: 'The doubled first year is no joke. My level-2 network alone covers a night out every month.', n: 'Priya S.', r: 'Beauty creator' },
  { q: 'No inventory, no awkward selling — I just recommend products I already use. Easiest yes ever.', n: 'Jordan L.', r: 'Esthetician' },
];

function Proof() {
  const ref = useReveal();
  return (
    <section ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
      <h2 className="cash-reveal font-display text-4xl md:text-5xl font-semibold text-center text-gray-900">Ambassadors are already glowing</h2>
      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {QUOTES.map((t, i) => (
          <figure key={t.n} className="cash-reveal rounded-3xl bg-tea-50 border border-tea-100 p-8" data-delay={String(i + 1)}>
            <div className="text-tea-400 text-5xl font-display leading-none">&ldquo;</div>
            <blockquote className="text-gray-800 leading-relaxed -mt-3">{t.q}</blockquote>
            <figcaption className="mt-6">
              <div className="font-semibold text-gray-900">{t.n}</div>
              <div className="text-sm text-tea-600">{t.r}</div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

// ─── FAQ ────────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'Does it really cost nothing to join?', a: 'Nothing. There are no joining fees, no monthly costs, and no inventory to buy. You earn from the moment your link is used.' },
  { q: 'How does the doubled first year work?', a: 'When you join with our special ambassador link, your direct referral rate is automatically doubled — 6% instead of 3% — for a full 12 months. After that you keep the standard 3 / 2 / 1% across three levels.' },
  { q: 'When and how do I get paid?', a: 'Commissions become referral cash once the order ships. You can redeem that cash for huge discounts at checkout — up to 100% off — on the products you love.' },
  { q: 'Do I need a big following?', a: 'Not at all. Most ambassadors simply share with friends, family, and their everyday circle. A personal recommendation outperforms a billboard.' },
  { q: 'Who can become an ambassador?', a: 'Anyone 21 or older with an AB8 Beauty account. Apply below and you’re approved instantly.' },
];

function Faq() {
  const ref = useReveal();
  const [open, setOpen] = useState(0);
  return (
    <section ref={ref} className="bg-tea-50 polka-pink">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <h2 className="cash-reveal font-display text-4xl md:text-5xl font-semibold text-center text-gray-900">Questions, answered</h2>
        <div className="cash-reveal mt-12 space-y-3" data-delay="1">
          {FAQS.map((f, i) => (
            <div key={i} className="rounded-2xl bg-white border border-gray-100 overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
                <span className="font-semibold text-gray-900">{f.q}</span>
                <span className={'text-tea-600 text-xl transition-transform ' + (open === i ? 'rotate-45' : '')}>+</span>
              </button>
              {open === i && <p className="px-6 pb-5 -mt-1 text-gray-600 leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Application form ─────────────────────────────────────────────────────────────
function ApplyForm({ inviteCode, formRef }) {
  const { user, login } = useAuthCtx();
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', dateOfBirth: '', email: '', password: '', tiktokHandle: '', instagramHandle: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const computeAge = (iso) => {
    const dob = new Date(iso);
    if (Number.isNaN(dob.getTime())) return NaN;
    const now = new Date();
    let age = now.getUTCFullYear() - dob.getUTCFullYear();
    const m = now.getUTCMonth() - dob.getUTCMonth();
    if (m < 0 || (m === 0 && now.getUTCDate() < dob.getUTCDate())) age--;
    return age;
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.dateOfBirth) e.dateOfBirth = 'Required';
    else {
      const age = computeAge(form.dateOfBirth);
      if (Number.isNaN(age)) e.dateOfBirth = 'Invalid date';
      else if (age < 21) e.dateOfBirth = 'You must be 21 or older to join';
      else if (age > 120) e.dateOfBirth = 'Please enter a valid date of birth';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (form.password.length < 8) e.password = 'At least 8 characters';
    else if (!/[A-Z]/.test(form.password)) e.password = 'Must include an uppercase letter';
    else if (!/[0-9]/.test(form.password)) e.password = 'Must include a number';
    if (!form.tiktokHandle.trim() && !form.instagramHandle.trim()) {
      e.handles = 'Add at least one — TikTok or Instagram.';
    }
    return e;
  };

  const maxDob = (() => {
    const d = new Date();
    d.setUTCFullYear(d.getUTCFullYear() - 21);
    return d.toISOString().slice(0, 10);
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await api.post('/ambassadors/join', { ...form, inviteCode: inviteCode || undefined });
      await login(form.email, form.password);
      toast.success('Welcome to the AB8 Beauty Ambassador family!');
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.data?.fields) {
        const fe = {};
        err.response.data.fields.forEach((f) => { fe[f.field] = f.message; });
        setErrors(fe);
      } else {
        toast.error(err.response?.data?.error || 'Could not complete your application');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Already signed in → don't show a second account form.
  if (user) {
    return (
      <section ref={formRef} id="apply" className="bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-gray-900">You&rsquo;re signed in, {user.firstName}</h2>
          <p className="text-gray-600 mt-4 max-w-xl mx-auto">
            {user.role === 'ambassador' || user.role === 'affiliate'
              ? 'You already have a rewards account — head to your dashboard to grab your link and QR code.'
              : 'Ambassador enrolment uses a fresh email-based application. Visit your dashboard or contact us to convert this account.'}
          </p>
          <Link to="/dashboard" className="btn-primary mt-8 px-8 py-4 text-base">Go to my dashboard →</Link>
        </div>
      </section>
    );
  }

  const Field = ({ name, label, type = 'text', placeholder, ...rest }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type} value={form[name]} onChange={set(name)} placeholder={placeholder}
        className="input-field" {...rest}
      />
      {errors[name] && <p className="text-red-500 text-xs mt-1">{errors[name]}</p>}
    </div>
  );

  return (
    <section ref={formRef} id="apply" className="relative bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
        {/* pitch column */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-tea-600">Apply now — instant approval</p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-gray-900 mt-3 leading-tight">
            Start your doubled first year today
          </h2>
          <p className="text-gray-600 mt-5 leading-relaxed">
            Create your ambassador account and we&rsquo;ll hand you a personal link and QR code on
            the spot. No fees. No inventory. Just the products you love and the cash you earn for
            sharing them.
          </p>
          <ul className="mt-7 space-y-3">
            {['6% referral cash for 12 months', 'Three levels of network rewards', 'Redeem cash for up to 100% off', 'Personal link + scannable QR code'].map((b) => (
              <li key={b} className="flex items-center gap-3 text-gray-700">
                <span className="grid place-items-center w-6 h-6 rounded-full bg-tea-100 text-tea-700 text-sm">✓</span>
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* form card */}
        <div className="rounded-3xl bg-tea-50 border border-tea-100 p-7 md:p-9 shadow-sm">
          {inviteCode && (
            <div className="mb-5 inline-flex items-center gap-1.5 rounded-xl bg-white border border-tea-200 px-4 py-3 text-sm text-tea-700">
              <Icon name="sparkles" className="w-4 h-4" /> Applying with invite <strong className="font-mono">{inviteCode}</strong>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field name="firstName" label="First name" placeholder="First name" />
              <Field name="lastName" label="Last name" placeholder="Last name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of birth</label>
              <input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} max={maxDob} className="input-field" />
              <p className="text-xs text-gray-500 mt-1">You must be 21 or older to join.</p>
              {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>}
            </div>
            <Field name="email" label="Email" type="email" placeholder="you@example.com" />
            <Field name="password" label="Create a password" type="password" placeholder="Min 8 chars, 1 uppercase, 1 number" />

            {/* Socials — "we just need your socials". At least one required. */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">TikTok</label>
                <div className="flex items-center input-field gap-1 px-3">
                  <span className="text-gray-400">@</span>
                  <input value={form.tiktokHandle} onChange={set('tiktokHandle')} placeholder="yourhandle"
                    maxLength={120} className="flex-1 bg-transparent border-0 focus:ring-0 outline-none p-0 py-2 min-w-0" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                <div className="flex items-center input-field gap-1 px-3">
                  <span className="text-gray-400">@</span>
                  <input value={form.instagramHandle} onChange={set('instagramHandle')} placeholder="yourhandle"
                    maxLength={120} className="flex-1 bg-transparent border-0 focus:ring-0 outline-none p-0 py-2 min-w-0" />
                </div>
              </div>
            </div>
            <p className="-mt-2 text-xs text-gray-500">Where do you post? Add at least one so we can see your glow.</p>
            {errors.handles && <p className="text-red-500 text-xs -mt-1">{errors.handles}</p>}

            <button type="submit" disabled={submitting} className="btn-primary w-full py-3.5 text-base">
              {submitting ? 'Creating your account…' : 'Become an Ambassador'}
            </button>
            <p className="text-center text-xs text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-tea-600 font-medium hover:underline">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────────
export default function AmbassadorsPage() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite') || '';
  const [invite, setInvite] = useState(null);
  const applyRef = useRef(null);

  // Validate the invite (and log a landing visit) when one is present.
  useEffect(() => {
    if (!inviteCode) return;
    api.get(`/ambassadors/invite/${encodeURIComponent(inviteCode)}`)
      .then((res) => setInvite(res.data))
      .catch(() => setInvite(null));
  }, [inviteCode]);

  const scrollToApply = useCallback(() => {
    applyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">
        <Hero invite={invite} onApply={scrollToApply} />
        <ValueProps />
        <EarningsCalculator onApply={scrollToApply} />
        <HowItWorks />
        <Tiers />
        <Proof />
        <ApplyForm inviteCode={inviteCode} formRef={applyRef} />
        <Faq />
      </main>
      <Footer />
    </div>
  );
}
